/**
 * Role → permission map (FE plan §3.5, BE reqs §7.13). Components check
 * permissions ("capabilities"), never role strings.
 *
 * Mirrors api-core-go `updraft/backend/common/commonroute/admin-permissions.go`
 * (`PermissionTable`) exactly; both sides assert the same matrix in their
 * tests, so a change made on one side only fails there. The JWT's `roles` claim
 * is copied from the signed-in company profile's `roles`, so `profile.roles` is
 * what the BE gates on.
 *
 * Roles are ADDITIVE: a profile's permissions are the union of its roles'.
 * DevOps-only routes (sweeps, raw CRUD writes) are not exposed in the app.
 */

export const ROLE_COMPANY = 'company';
export const ROLE_COMPANY_MARKETING = 'company_marketing';
export const ROLE_COMPANY_CRM = 'company_crm';
export const ROLE_ADMIN_TAX = 'admin_tax';
export const ROLE_ADMIN_MONEY = 'admin_money';
export const ROLE_ADMIN_OWNER = 'admin_owner';
export const ROLE_ADMIN_READ = 'admin_read';
/** Sends a message to a user from the Eater / Chef Message tab. Granted on its own. */
export const ROLE_USER_MESSAGE = 'user_message';

export type Capability =
  | 'core.read'
  | 'core.write'
  | 'marketing.write'
  | 'crm.write'
  | 'money.health.read'
  | 'money.read'
  | 'levers.write'
  | 'tax.write'
  | 'registrations.write'
  | 'moneyIn.write'
  | 'payouts.write'
  | 'ledger.adjust'
  | 'buffer.write'
  | 'recon.run'
  | 'alerts.ack'
  | 'global.write'
  | 'alerts.bsc.read'
  | 'alerts.bsc.ack'
  | 'alerts.campaign.read'
  | 'alerts.campaign.ack'
  | 'user.message';

const ADMIN_ALL = [ROLE_ADMIN_TAX, ROLE_ADMIN_MONEY, ROLE_ADMIN_OWNER, ROLE_ADMIN_READ];
const ADMIN_WRITER = [ROLE_ADMIN_TAX, ROLE_ADMIN_MONEY, ROLE_ADMIN_OWNER];
const MONEY_OPS = [ROLE_ADMIN_MONEY, ROLE_ADMIN_OWNER];
const TAX_OPS = [ROLE_ADMIN_TAX, ROLE_ADMIN_OWNER];

export const PERMISSIONS: Record<Capability, readonly string[]> = {
  'core.read': [ROLE_COMPANY],
  'core.write': [ROLE_COMPANY],
  'marketing.write': [ROLE_COMPANY_MARKETING],
  'crm.write': [ROLE_COMPANY_CRM],
  'money.health.read': [ROLE_COMPANY, ...ADMIN_ALL],
  'money.read': ADMIN_ALL,
  'levers.write': ADMIN_WRITER,
  'tax.write': TAX_OPS,
  'registrations.write': ADMIN_WRITER,
  'moneyIn.write': MONEY_OPS,
  'payouts.write': MONEY_OPS,
  'ledger.adjust': [ROLE_ADMIN_OWNER],
  'buffer.write': MONEY_OPS,
  'recon.run': MONEY_OPS,
  'alerts.ack': ADMIN_WRITER,
  'global.write': [ROLE_ADMIN_OWNER],
  'alerts.bsc.read': [ROLE_COMPANY_CRM, ROLE_ADMIN_OWNER, ROLE_ADMIN_READ],
  'alerts.bsc.ack': [ROLE_COMPANY_CRM, ROLE_ADMIN_OWNER],
  'alerts.campaign.read': [ROLE_COMPANY_MARKETING, ROLE_ADMIN_OWNER, ROLE_ADMIN_READ],
  'alerts.campaign.ack': [ROLE_COMPANY_MARKETING, ROLE_ADMIN_OWNER],
  'user.message': [ROLE_USER_MESSAGE],
};

/** Display names for roles (header, account page, disabled-button tooltips). */
export const ROLE_LABEL: Record<string, string> = {
  [ROLE_COMPANY]: 'Staff',
  [ROLE_COMPANY_MARKETING]: 'Marketing',
  [ROLE_COMPANY_CRM]: 'CRM',
  [ROLE_ADMIN_TAX]: 'Tax',
  [ROLE_ADMIN_MONEY]: 'Money',
  [ROLE_ADMIN_OWNER]: 'Owner',
  [ROLE_ADMIN_READ]: 'Auditor',
  [ROLE_USER_MESSAGE]: 'Messaging',
};

export const ALL_CAPABILITIES = Object.keys(PERMISSIONS) as Capability[];

export type Capabilities = ReadonlySet<Capability>;

export function capabilitiesFor(roles: readonly string[] | null | undefined): Capabilities {
  const held = new Set(roles ?? []);
  return new Set(ALL_CAPABILITIES.filter((cap) => PERMISSIONS[cap].some((role) => held.has(role))));
}

export function can(capabilities: Capabilities, capability: Capability): boolean {
  return capabilities.has(capability);
}

export function hasAnyAdminAccess(capabilities: Capabilities): boolean {
  return capabilities.size > 0;
}

/** Why a control is disabled: the roles that would allow it, by display name. */
export function missingReason(capability: Capability): string {
  const names = PERMISSIONS[capability].map((r) => ROLE_LABEL[r] ?? r);
  return `Needs the ${names.join(' or ')} role`;
}
